import {IComposition} from "../../Inventory/composition";
import {IInterchangedComponent} from "./interchanged-component";
import {ICompositionOverride} from "../../Inventory/composition-override";
import {ModificationLineTransfer} from "../../../_models/_booking/composition-override-linker-transfer";

export interface ICompositionData {
  compositeId: string;
  composition: IComposition;
  interchangedComponents: IInterchangedComponent;
  override?: ICompositionOverride;
  quantity: number;
  singlePrice: number;
  /**
   * The line's rendered modification/default lines — `├ 4 cl Premium Gin`, `└ ohne Limette`, `mit 2 l Sirup` —
   * already ordered by slot and already including the untouched-but-interchangeable slot defaults (#423).
   *
   * The backend renders them ONCE, through the same formatter the register basket, the receipt and the ESC/POS
   * ticket use (`OrderPositionModificationFormatter`), because building them needs the composition's slot graph
   * that no client holds. It ships an **empty array, never `null`**, exactly like
   * `CompositionOverrideLinkerTransfer.modificationLines` — the very same wire type, deliberately reused so the
   * surfaces cannot drift.
   *
   * Optional on the TYPE only, and only so a client still talking to a pre-#423 monolith type-checks: read it
   * through a `?? []`, never assume it is present (`open-tables.store.ts`'s `toItems` does).
   */
  modificationLines?: ModificationLineTransfer[];
}

export class CompositionDataHelpers {
  public static getTotalPrice(data: ICompositionData) {
    return data.quantity * data.singlePrice;
  }

  /**
   * Whether this line still has something **open** on it — i.e. whether it is unpaid.
   *
   * `quantity` on a booking reference's composition data is **not** the booked quantity: the monolith nets it
   * per composite in `API.Core.Extensions.Models.Booking.BookingReferenceEntityExtensions.GroupOpenBalance`,
   * which multiplies every order's quantity by `BookingType.OpenBalanceSign()` — bookings add, payments and the
   * closing marker subtract — and groups the result. What arrives here is therefore already
   * "booked minus paid", and `0` means the line has been settled in full.
   *
   * That is why the test is on the quantity and never on the price: a `0,00 €` line can be a legitimately free
   * item that is still owed, and hiding it would hide a real row. The MAUI register's table view filters the
   * same field (`BookingContentController`'s `Filter(x => x.Position.Quantity > 0)`), so both surfaces agree on
   * every ordinary line.
   *
   * The one deliberate difference is the sign. This helper uses `!== 0` rather than `> 0` because it drives a
   * **read-only** overview whose total must always equal the sum of the lines it shows: a negative net balance
   * (more paid than booked) still belongs on the card, and dropping it would leave the footer disagreeing with
   * the rows above it. The register excludes it because its list is a selection list for taking payment, where
   * an over-paid line is not something you can select. So the two can differ on an over-paid line, and that is
   * intended — do not "align" them without re-checking which invariant each surface owes its user.
   */
  public static hasOpenBalance(data: ICompositionData): boolean {
    return data.quantity !== 0;
  }
}
